import { c as _c } from "react/compiler-runtime";
import { IcoArrowRightSmall16, IcoFolders16 } from '@onefootprint/icons';
import type { ChatSessionResponse } from '@onefootprint/request-types/dashboard';
import { Tooltip } from '@onefootprint/ui';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { agentBuilderUrl } from '@/config/constants';
import useMobileLayout from '@/hooks/use-mobile-layout';
import useChatWidgetLayout from '../../hooks/use-chat-widget-layout';
import useChatWidgetOpen from '../../hooks/use-chat-widget-open';
import type { ResourceMessage } from '../../hooks/use-chat-ws';
import useDocumentsPanelOpen from '../../hooks/use-documents-panel-open';
import { resolveNavigationChatSessionId } from '../../utils/resolve-navigation-chat-session-id';
import { presentAgentSuggestions, presentResource, type ResourcePresentation, resourceGroup, resourceKey } from '../../utils/resource-presentation';
import { revealResourceInExplorer } from '../../utils/reveal-resource';
import { useChatResourceListener } from '../chat-widget/chat-resource-listener-context';
import { EntityResourceName } from '../resource-name';

// Inner content of the aggregate suggestions card — lives inside the card's <button>, so it must be
// spans (phrasing content) only.
const ResourceCardBody = t0 => {
  const $ = _c(21);
  const {
    p
  } = t0;
  let t1;
  if ($[0] !== p.name || $[1] !== p.summaries) {
    t1 = (p.summaries ?? []).join(", ") || p.name;
    $[0] = p.name;
    $[1] = p.summaries;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const subtitle = t1;
  let t2;
  if ($[3] !== p.icon) {
    t2 = <span className="flex items-center justify-center size-8 rounded-full bg-secondary text-primary shrink-0">{p.icon}</span>;
    $[3] = p.icon;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== p.createdTitle) {
    t3 = <span className="text-label-3 text-primary truncate">{p.createdTitle}</span>;
    $[5] = p.createdTitle;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== subtitle) {
    t4 = subtitle && <span className="text-caption-2 text-tertiary truncate">{subtitle}</span>;
    $[7] = subtitle;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <span className="flex flex-col gap-1 min-w-0 flex-1">{t3}{t4}</span>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t2 || $[13] !== t5) {
    t6 = <span className="flex items-center gap-3 flex-1 min-w-0">{t2}{t5}</span>;
    $[12] = t2;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = p.actionLabel ?? "Open";
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <IcoArrowRightSmall16 />;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t7) {
    t9 = <span className="shrink-0 inline-flex items-center gap-1 h-7 px-2 rounded-md border border-primary bg-primary shadow-sm text-label-3 text-primary">{t7}{t8}</span>;
    $[16] = t7;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t6 || $[19] !== t9) {
    t10 = <>{t6}{t9}</>;
    $[18] = t6;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
};

// Where this card sits in a joined run of cards. A run reads as one block: only the outer corners
// round and the rows sit flush, no divider. 'single' is a standalone card (both corners round).
export type ResourceCardPosition = 'single' | 'first' | 'middle' | 'last';
const POSITION_SHAPE: Record<ResourceCardPosition, string> = {
  single: 'rounded-default',
  first: 'rounded-t-default',
  middle: '',
  last: 'rounded-b-default'
};

// Resource marker — a one-line breadcrumb that opens the Context Explorer and flashes this
// resource's row there (its `data-resource-key` is also the anchor the explorer scrolls back to).
const ResourceCreatedCard = t0 => {
  const $ = _c(48);
  const {
    message,
    position: t1,
    indented: t2
  } = t0;
  const position = t1 === undefined ? "single" : t1;
  const indented = t2 === undefined ? false : t2;
  const {
    t
  } = useTranslation("copilot");
  const documentsPanel = useDocumentsPanelOpen();
  const router = useRouter();
  if (!message.resource) {
    return null;
  }
  const resource = message.resource;
  let inExplorer;
  let key;
  let p;
  let subjectFallback;
  let subjectFpId;
  let subtitle;
  let t3;
  let t4;
  if ($[0] !== resource || $[1] !== t) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      p = presentResource(resource, t);
      if (!p) {
        t4 = null;
        break bb0;
      }
      let t5;
      if ($[10] !== resource) {
        t5 = resourceKey(resource);
        $[10] = resource;
        $[11] = t5;
      } else {
        t5 = $[11];
      }
      key = t5;
      let t6;
      if ($[12] !== resource) {
        t6 = resourceGroup(resource);
        $[12] = resource;
        $[13] = t6;
      } else {
        t6 = $[13];
      }
      inExplorer = t6 !== null;
      subjectFpId = resource.kind === "investigation" ? resource.fp_id ?? null : resource.kind === "entity" && !resource.name ? resource.id : null;
      subjectFallback = resource.kind === "entity" ? key : "";
      subtitle = (p.summaries ?? []).join(", ") || p.name;
      t3 = p.name && !p.name.startsWith("fp_") ? p.name : null;
    }
    $[0] = resource;
    $[1] = t;
    $[2] = inExplorer;
    $[3] = key;
    $[4] = p;
    $[5] = subjectFallback;
    $[6] = subjectFpId;
    $[7] = subtitle;
    $[8] = t3;
    $[9] = t4;
  } else {
    inExplorer = $[2];
    key = $[3];
    p = $[4];
    subjectFallback = $[5];
    subjectFpId = $[6];
    subtitle = $[7];
    t3 = $[8];
    t4 = $[9];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  const spokenName = t3;
  let t5;
  if ($[14] !== documentsPanel || $[15] !== key) {
    t5 = () => {
      documentsPanel.openTo("resources");
      revealResourceInExplorer(key);
    };
    $[14] = documentsPanel;
    $[15] = key;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const handleShowInExplorer = t5;
  let t6;
  if ($[17] !== resource || $[18] !== router || $[19] !== t) {
    t6 = event => {
      const chatSessionId = resolveNavigationChatSessionId({
        pathname: router.pathname,
        query: router.query
      });
      const presentation = presentResource(resource, t, {
        chatSessionId
      });
      if (!presentation) {
        return;
      }
      if (event.metaKey || event.ctrlKey || presentation.href.startsWith("http")) {
        window.open(presentation.href, "_blank");
        return;
      }
      router.push(presentation.href);
    };
    $[17] = resource;
    $[18] = router;
    $[19] = t;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const handleOpen = t6;
  const t7 = `group/marker flex items-center gap-1 w-full ${indented ? "pl-8" : "pl-2"} pr-1.5 bg-secondary hover:bg-hover transition-colors ${POSITION_SHAPE[position]}`;
  const t8 = `${p.actionLabel ?? "Open"}: ${spokenName ?? p.createdTitle}`;
  let t9;
  if ($[21] !== p.icon) {
    t9 = <span data-resource-icon={true} className="shrink-0 flex items-center justify-center size-6 text-primary">{p.icon}</span>;
    $[21] = p.icon;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== p.createdTitle) {
    t10 = <span className="shrink-0 text-label-3 text-primary truncate">{p.createdTitle}</span>;
    $[23] = p.createdTitle;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== subjectFallback || $[26] !== subjectFpId || $[27] !== subtitle) {
    t11 = subjectFpId ? <span className="text-caption-2 text-tertiary truncate"><EntityResourceName fpId={subjectFpId} fallback={subjectFallback} /></span> : subtitle && <span className="text-caption-2 text-tertiary truncate">{subtitle}</span>;
    $[25] = subjectFallback;
    $[26] = subjectFpId;
    $[27] = subtitle;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t10 || $[30] !== t11) {
    t12 = <span className="flex items-baseline gap-1.5 min-w-0 flex-1 leading-none">{t10}{t11}</span>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== handleOpen || $[33] !== t12 || $[34] !== t8 || $[35] !== t9) {
    t13 = <button type="button" onClick={handleOpen} aria-label={t8} className="flex-1 min-w-0 flex items-center gap-1 py-1.5 text-left cursor-pointer outline-none rounded focus-visible:ring-1 focus-visible:ring-accent focus-visible:ring-inset">{t9}{t12}</button>;
    $[32] = handleOpen;
    $[33] = t12;
    $[34] = t8;
    $[35] = t9;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== handleShowInExplorer || $[38] !== inExplorer || $[39] !== p.createdTitle || $[40] !== spokenName || $[41] !== t) {
    t14 = inExplorer && <Tooltip content={t(_temp)}><button type="button" onClick={handleShowInExplorer} aria-label={`${spokenName ? `${p.createdTitle}: ${spokenName}` : p.createdTitle} — ${t(_temp2)}`} className="shrink-0 size-7 flex items-center justify-center rounded text-tertiary hover:text-primary hover:bg-hover-control opacity-0 max-md:opacity-100 transition outline-none cursor-pointer group-hover/marker:opacity-100 focus-visible:opacity-100 focus-visible:ring-1 focus-visible:ring-accent focus-visible:ring-inset"><IcoFolders16 className="shrink-0" /></button></Tooltip>;
    $[37] = handleShowInExplorer;
    $[38] = inExplorer;
    $[39] = p.createdTitle;
    $[40] = spokenName;
    $[41] = t;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== key || $[44] !== t13 || $[45] !== t14 || $[46] !== t7) {
    t15 = <div data-resource-key={key} className={t7}>{t13}{t14}</div>;
    $[43] = key;
    $[44] = t13;
    $[45] = t14;
    $[46] = t7;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  return t15;
};

// The aggregate "Percy proposed N changes" card, rendered once per collapsed batch (see chat-panel).
// Suggestions still open the builder's Changes panel directly, so this stays a full card.
export const AgentSuggestionsCard = t0 => {
  const $ = _c(18);
  const {
    agentId,
    count,
    summaries,
    session
  } = t0;
  const {
    t
  } = useTranslation("copilot");
  const router = useRouter();
  const {
    setMode
  } = useChatWidgetLayout();
  const isMobile = useMobileLayout();
  const {
    close
  } = useChatWidgetOpen();
  const resourceListener = useChatResourceListener();
  let t1;
  if ($[0] !== agentId || $[1] !== count || $[2] !== summaries || $[3] !== t) {
    t1 = presentAgentSuggestions(agentId, count, summaries, t);
    $[0] = agentId;
    $[1] = count;
    $[2] = summaries;
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const p = t1;
  let t2;
  if ($[5] !== agentId || $[6] !== close || $[7] !== isMobile || $[8] !== resourceListener || $[9] !== router || $[10] !== session.sessionId || $[11] !== setMode) {
    t2 = event => {
      const chatSessionId = resolveNavigationChatSessionId({
        sessionId: session.sessionId,
        pathname: router.pathname,
        query: router.query
      });
      const href = agentBuilderUrl(agentId, {
        chatSessionId,
        tab: "changes"
      });
      if (event.metaKey || event.ctrlKey) {
        window.open(href, "_blank");
        return;
      }
      if (resourceListener?.openSuggestions?.(agentId)) {
        if (isMobile) {
          close();
        }
        return;
      }
      setMode("sidebar");
      router.push(href);
    };
    $[5] = agentId;
    $[6] = close;
    $[7] = isMobile;
    $[8] = resourceListener;
    $[9] = router;
    $[10] = session.sessionId;
    $[11] = setMode;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const handleOpen = t2;
  let t3;
  if ($[13] !== p) {
    t3 = <ResourceCardBody p={p} />;
    $[13] = p;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== handleOpen || $[16] !== t3) {
    t4 = <button type="button" onClick={handleOpen} className="flex items-center gap-4 w-full text-left mb-2 rounded-default border border-tertiary bg-primary p-3 hover:bg-hover transition-colors">{t3}</button>;
    $[15] = handleOpen;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  return t4;
};
export default ResourceCreatedCard;
function _temp($) {
  return $.chat.resources.showInExplorer;
}
function _temp2($_0) {
  return $_0.chat.resources.showInExplorer;
}